import React, { useEffect, useState } from "react";

export type SolutionCategory =
  | "BESS"
  | "EV Infrastructure"
  | "Home"
  | "Ships"
  | "Tools"
  | "Agriculture"
  | "Data Center"
  | "UAV";

interface SolutionGridProps {
  selectedSolution: SolutionCategory | null; // Allow null
  onSolutionChange: (solution: SolutionCategory) => void;
  isScrolling?: boolean;
}

const SolutionGrid: React.FC<SolutionGridProps> = ({
  selectedSolution,
  onSolutionChange,
  isScrolling = false,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [highlightedSolution, setHighlightedSolution] = useState<SolutionCategory | null>(null);

  const solutionCategories: SolutionCategory[] = [
    "BESS",
    "EV Infrastructure",
    "Home",
    "Ships",
    "Tools",
    "Agriculture",
    "Data Center",
    "UAV",
  ];

  const solutionImages: Record<SolutionCategory, string> = {
    BESS: "/solutions/battery.png",
    "EV Infrastructure": "/solutions/carev.png",
    Home: "/solutions/home.png",
    Ships: "/solutions/ship.png",
    Tools: "/solutions/tools.png",
    Agriculture: "/solutions/tractor.png",
    "Data Center": "/solutions/datacenter.svg",
    UAV: "/solutions/arial.png",
  };

  useEffect(() => {
    if (isScrolling && selectedSolution) {
      setIsAnimating(true);
      setHighlightedSolution(selectedSolution);
      
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setHighlightedSolution(null);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isScrolling, selectedSolution]);

  const handleClick = (category: SolutionCategory) => {
    setIsAnimating(true);
    setHighlightedSolution(category);
    onSolutionChange(category);
    
    setTimeout(() => {
      setIsAnimating(false);
      setHighlightedSolution(null);
    }, 1000);
  };

  return (
    <div className="mb-2">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 md:gap-4 p-2">
        {solutionCategories.map((category) => {
          const isSelected = selectedSolution === category; // This will work with null
          const isHighlighted = highlightedSolution === category && isAnimating;
          
          return (
            <button
              key={category}
              onClick={() => handleClick(category)}
              className={`
                relative flex flex-col items-center p-4 rounded-full transition-all duration-300 
                hover:bg-gray-50 min-h-[120px] justify-start
                ${isSelected ? "text-sky-600 bg-sky-50 border-2 border-sky-300" : "text-gray-700 hover:text-gray-900 border border-transparent"}
                ${isHighlighted ? "ring-4 ring-sky-200 animate-pulse" : ""}
              `}
            >
              {isHighlighted && (
                <div className="absolute inset-0 rounded-full bg-sky-100 animate-ping opacity-60"></div>
              )}
              
              <div
                className={`
                relative w-16 h-16 md:w-24 md:h-24 mb-3 flex items-center justify-center
                transition-all duration-300 overflow-hidden
                ${isSelected ? "scale-110" : ""}
              `}
              >
                {solutionImages[category] ? (
                  <img
                    src={solutionImages[category]}
                    alt={category}
                    className={`object-cover transition-transform duration-300 ${isSelected ? "scale-105" : ""}`}
                  />
                ) : (
                  <div
                    className={`text-xs text-center ${
                      isSelected ? "text-sky-500" : "text-gray-400"
                    }`}
                  >
                    {category.split(" ")[0]}
                  </div>
                )}
              </div>

              <div
                className={`
                relative font-semibold text-md text-center leading-tight
                ${isSelected ? "text-sky-600" : "text-gray-700"}
              `}
              >
                {category}
              </div>
              
              {isSelected && (
                <div className="absolute bottom-2 w-8 h-1 bg-sky-500 rounded-full"></div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SolutionGrid;